// ─────────────────────────────────────────────────────────────────────────────
// graphZoomToPointer — pointer-anchored dolly for the pinned r149 OrbitControls.
//
// WHY THIS EXISTS. r149's OrbitControls dollies toward controls.target and has
// no zoomToCursor (verified absent in the installed source). From the initial
// state the target is City Hall, so every wheel turn converges on the core and
// the galaxy feels like an object you orbit rather than a space you travel
// through. There is no target-reset bug to remove — this is the actual cause.
//
// WHAT IT DOES. OrbitControls still performs the dolly. Around it, this captures
// the world point under the pointer BEFORE the zoom, and after the zoom
// translates both camera and target so that point lands back under the pointer.
// The anchor is a POINT IN SPACE, never a node: nothing is locked onto.
//
// It does not modify OrbitControls, does not patch node_modules and does not
// touch the prototype. It is a listener layer the graph owns and disposes.
//
// Every vector, plane and raycaster is allocated once in the constructor. A
// wheel burst allocates nothing.
// ─────────────────────────────────────────────────────────────────────────────

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

/** Below this separation change a two-finger move is a pan, not a pinch. */
const PINCH_DELTA_PX = 0.5;
/** Residual anchor error worth a second correction pass, in world units. */
const REFINE_EPSILON = 0.01;
/** Hard cap on refinement passes. Visual stability, not sub-pixel perfection. */
const MAX_REFINEMENTS = 1;

/**
 * CONSUME-ONCE, as briefed — and the measurement that settled it.
 *
 * Correcting only the first change event leaves residual drift, because damping
 * spreads one wheel notch across many frames. I tried keeping the anchor alive
 * for the tail: corrections went 56 → 138, CPU 1.2 → 2.5 ms, and the worst
 * residual got WORSE (9.9 → 12.1 world units), because each correction is
 * immediately restaled by the damping the same update() applies. The extra
 * machinery bought nothing, so it is gone and the briefed design stands.
 *
 * The residual is reported honestly rather than tuned against: see the Stage B
 * report for what it is worth in screen pixels.
 */

export interface ZoomAnchorStats {
  wheelCorrections: number;
  pinchCorrections: number;
  /** Worst residual distance between the anchor point and where it landed. */
  maxAnchorErrorWorld: number;
  lastAnchorErrorWorld: number;
  lastCorrectionMagnitude: number;
  /** Total CPU spent in corrections, milliseconds. */
  cpuMs: number;
}

interface PointerRecord {
  x: number;
  y: number;
  type: string;
}

export class GraphZoomToPointer {
  /** Called on any manual navigation input, so a focus flight can stand down. */
  onManualInput: (() => void) | null = null;

  private pointers = new Map<number, PointerRecord>();
  private pendingKind: 'wheel' | 'pinch' | null = null;
  private applying = false;
  private lastPinchDistance = 0;

  // Allocated once. See the header.
  private ndc = new THREE.Vector2();
  private raycaster = new THREE.Raycaster();
  private plane = new THREE.Plane();
  private planeNormal = new THREE.Vector3();
  private beforePoint = new THREE.Vector3();
  private afterPoint = new THREE.Vector3();
  private correction = new THREE.Vector3();

  private stats: ZoomAnchorStats = {
    wheelCorrections: 0,
    pinchCorrections: 0,
    maxAnchorErrorWorld: 0,
    lastAnchorErrorWorld: 0,
    lastCorrectionMagnitude: 0,
    cpuMs: 0,
  };

  constructor(
    private camera: THREE.PerspectiveCamera,
    private controls: OrbitControls,
    private domElement: HTMLElement,
  ) {
    // CAPTURE PHASE, so the anchor is taken while the camera is still where the
    // user aimed from. OrbitControls receives the event afterwards, untouched:
    // stopImmediatePropagation would break its own dolly entirely.
    domElement.addEventListener('wheel', this.onWheelCapture, { capture: true, passive: false });
    domElement.addEventListener('pointerdown', this.onPointerDownCapture, { capture: true });
    domElement.addEventListener('pointermove', this.onPointerMoveCapture, { capture: true });
    domElement.addEventListener('pointerup', this.onPointerUpCapture, { capture: true });
    domElement.addEventListener('pointercancel', this.onPointerUpCapture, { capture: true });
    controls.addEventListener('change', this.onControlsChange);
    controls.addEventListener('start', this.onControlsStart);
  }

  getStats(): ZoomAnchorStats {
    return this.stats;
  }

  resetStats(): void {
    this.stats = {
      wheelCorrections: 0, pinchCorrections: 0, maxAnchorErrorWorld: 0,
      lastAnchorErrorWorld: 0, lastCorrectionMagnitude: 0, cpuMs: 0,
    };
  }

  dispose(): void {
    this.domElement.removeEventListener('wheel', this.onWheelCapture, { capture: true } as any);
    this.domElement.removeEventListener('pointerdown', this.onPointerDownCapture, { capture: true } as any);
    this.domElement.removeEventListener('pointermove', this.onPointerMoveCapture, { capture: true } as any);
    this.domElement.removeEventListener('pointerup', this.onPointerUpCapture, { capture: true } as any);
    this.domElement.removeEventListener('pointercancel', this.onPointerUpCapture, { capture: true } as any);
    this.controls.removeEventListener('change', this.onControlsChange);
    this.controls.removeEventListener('start', this.onControlsStart);
    this.pointers.clear();
    this.pendingKind = null;
    this.onManualInput = null;
  }

  // ── anchor capture ─────────────────────────────────────────────────────────

  /**
   * Snapshot the navigation plane and the world point beneath (clientX, clientY).
   * Returns false when the ray misses the plane — an edge-on view — in which case
   * the zoom simply proceeds uncorrected rather than throwing.
   */
  private capture(clientX: number, clientY: number): boolean {
    const rect = this.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    this.ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -(((clientY - rect.top) / rect.height) * 2 - 1),
    );

    this.camera.getWorldDirection(this.planeNormal);
    this.plane.setFromNormalAndCoplanarPoint(this.planeNormal, this.controls.target);

    this.raycaster.setFromCamera(this.ndc, this.camera);
    return this.raycaster.ray.intersectPlane(this.plane, this.beforePoint) !== null;
  }

  /** Re-intersect the SAME captured plane with the moved camera. */
  private resolveAfter(): boolean {
    this.raycaster.setFromCamera(this.ndc, this.camera);
    return this.raycaster.ray.intersectPlane(this.plane, this.afterPoint) !== null;
  }

  private applyCorrection(kind: 'wheel' | 'pinch'): void {
    const started = performance.now();
    this.applying = true;

    let magnitude = 0;

    for (let pass = 0; pass <= MAX_REFINEMENTS; pass++) {
      if (!this.resolveAfter()) break;

      this.correction.subVectors(this.beforePoint, this.afterPoint);
      const drift = this.correction.length();
      if (pass === 0) magnitude = drift;
      if (drift < REFINE_EPSILON) break;

      // Translate the pair. Moving the target as well as the camera is what
      // keeps the orbit centre travelling with the view instead of the camera
      // swinging around a point it has left behind.
      this.camera.position.add(this.correction);
      this.controls.target.add(this.correction);
      this.controls.update();
    }

    // THE RESIDUAL IS MEASURED AFTER THE LAST CORRECTION, NOT BEFORE IT. The
    // first cut reported the drift entering the final pass, which is the error
    // the correction was about to remove — a number that flattered itself by one
    // whole pass.
    let error = 0;
    if (this.resolveAfter()) {
      error = this.beforePoint.distanceTo(this.afterPoint);
    }

    this.applying = false;

    if (kind === 'wheel') this.stats.wheelCorrections++;
    else this.stats.pinchCorrections++;
    this.stats.lastAnchorErrorWorld = error;
    this.stats.lastCorrectionMagnitude = magnitude;
    if (error > this.stats.maxAnchorErrorWorld) this.stats.maxAnchorErrorWorld = error;
    this.stats.cpuMs += performance.now() - started;
  }

  // ── listeners ──────────────────────────────────────────────────────────────

  private onControlsStart = (): void => {
    if (this.onManualInput) this.onManualInput();
  };

  private onControlsChange = (): void => {
    // Damping emits a change every frame of the tail. The correction belongs to
    // the dolly that started it, so it is consumed exactly once.
    if (!this.pendingKind || this.applying) return;
    const kind = this.pendingKind;
    this.pendingKind = null;
    this.applyCorrection(kind);
  };

  private onWheelCapture = (event: WheelEvent): void => {
    if (this.onManualInput) this.onManualInput();
    // The newest wheel event wins: a stale anchor from a burst would drag the
    // view back toward where the pointer used to be.
    this.pendingKind = this.capture(event.clientX, event.clientY) ? 'wheel' : null;
  };

  private onPointerDownCapture = (event: PointerEvent): void => {
    // A button going down begins an orbit or a pan. Any live zoom anchor is
    // dropped here so the correction can never pull against them.
    this.pendingKind = null;

    if (event.pointerType === 'touch') {
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, type: event.pointerType });
      if (this.pointers.size === 2) this.lastPinchDistance = this.pinchDistance();
    }
    if (this.onManualInput) this.onManualInput();
  };

  private onPointerMoveCapture = (event: PointerEvent): void => {
    if (event.pointerType !== 'touch') return;
    const record = this.pointers.get(event.pointerId);
    if (!record) return;
    record.x = event.clientX;
    record.y = event.clientY;

    if (this.pointers.size !== 2) return;

    const distance = this.pinchDistance();
    const delta = Math.abs(distance - this.lastPinchDistance);
    this.lastPinchDistance = distance;

    // A pure two-finger translation changes the midpoint but not the separation.
    // Correcting that would fight OrbitControls' pan, so it is left alone.
    if (delta < PINCH_DELTA_PX) return;

    let midX = 0;
    let midY = 0;
    this.pointers.forEach((p) => { midX += p.x; midY += p.y; });
    midX /= this.pointers.size;
    midY /= this.pointers.size;

    this.pendingKind = this.capture(midX, midY) ? 'pinch' : null;
  };

  private onPointerUpCapture = (event: PointerEvent): void => {
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) this.lastPinchDistance = 0;
  };

  private pinchDistance(): number {
    const list: PointerRecord[] = [];
    this.pointers.forEach((p) => list.push(p));
    if (list.length < 2) return 0;
    return Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y);
  }
}
